import styled, { css } from 'styled-components';
import LinkComponent from '@components/link';
import Section from '@components/section';
import { generateScales } from '@helpers/generate-media-queries';
import Image from '@components/image';
import { defaultTheme } from '@styles/theme.styled';

export const StyledSection = styled(Section)``;

export const TitleContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 0.5rem;
    margin: 0 auto;
    width: 100%;

    &::before {
      content: '';
      display: block;
      width: 5rem;
      height: 0.375rem;
      background-color: ${theme.colors.primary};
      border-radius: 0.1875rem;
      margin-bottom: 1rem;

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        margin-bottom: 0.5rem;
        width: 4rem;
      }
    }
  `}
`;

export const TextContent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 1.25rem;
    width: 100%;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      row-gap: 1rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      row-gap: 1rem;
    }
  `}
`;

export const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 2rem;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  position: relative;
`;

export const StyledImage = styled(Image)`
  ${({ theme }) => css`
    mix-blend-mode: multiply;
    max-height: 5rem;
    width: 100%;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      max-height: 4rem;
    }
  `}
`;

export const StyledLinkComponent = styled(LinkComponent)`
  ${({ theme }) => css`
    width: fit-content;
    margin: 0;
    background-color: ${theme.colors.primary};
    color: ${theme.colors.black};
    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.body.desktop, theme)}
    transition: transform 0.125s ease-in-out;

    &:hover {
      transform: scale(1.0375);
      background-color: ${theme.colors.primary}BF;
    }

    &:active {
      transform: scale(0.9625);
    }
  `}
`;

export const StyledItemLinkComponent = styled(LinkComponent)`
  ${({ theme }) => css`
    ${generateScales('font-size', theme.fontSize.small.mobile, theme.fontSize.small.desktop, theme)}
    margin: 0;
  `}
`;

interface FeatureWrapperProps {
  $imageAlignment?: 'default' | 'left' | 'right';
  $backgroundColor?: keyof typeof defaultTheme.backgroundColors;
}

export const FeatureWrapper = styled.div<FeatureWrapperProps>`
  ${({ theme, $imageAlignment, $backgroundColor }) => css`
    display: grid;
    grid-template-columns: 2fr 3fr;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 4rem;
    background-color: ${$backgroundColor === 'white'
      ? theme.backgroundColors.white
      : `${theme.backgroundColors.light}66`};
    border-radius: ${theme.borderRadius.large};
    padding: ${$backgroundColor === 'white' ? '0 3.5rem' : '6rem 4rem 6rem 4rem'};
    flex-direction: ${$imageAlignment === 'left' ? 'row' : 'row-reverse'};

    @media only screen and (max-width: ${theme.breakpoints.xxl}) {
      padding: ${$backgroundColor === 'white' ? '0 3.5rem' : '6rem 3.5rem 6rem 3.5rem'};
    }

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      padding: ${$backgroundColor === 'white' ? '0 3.5rem' : '4rem 3.5rem 4rem 3.5rem'};
      gap: 3.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      padding: ${$backgroundColor === 'white' ? '0 2.5rem' : '3.5rem 3rem 3.5rem 3rem'};
      gap: 2rem;
      grid-template-columns: 1fr;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      gap: 1rem;
      padding: ${$backgroundColor === 'white' ? '0 1.5rem' : '4rem 2.5rem'};
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      padding: ${$backgroundColor === 'white' ? '0 1rem' : '3rem 1rem'};
      gap: 2rem;
      border-radius: calc(${theme.borderRadius.large} / 2);
    }
  `}
`;
